"use client";

import type { PanchangResponse, PanchangPlanet } from "@/lib/api";
import { NakshatraLabel, PlanetLabel, RashiLabel, RashiPositionLabel } from "./Panchang.labels";

export const RASHI_HINDI: Record<string, string> = {
  mesh: "मेष",
  vrishabh: "वृषभ",
  mithun: "मिथुन",
  kark: "कर्क",
  singh: "सिंह",
  kanya: "कन्या",
  tula: "तुला",
  vrishchik: "वृश्चिक",
  dhanu: "धनु",
  makar: "मकर",
  kumbh: "कुंभ",
  meen: "मीन",
};

export const PLANET_LABELS: Record<string, { en: string; hi: string; symbol: string }> = {
  sun: { en: "Sun", hi: "सूर्य", symbol: "☉" },
  moon: { en: "Moon", hi: "चंद्र", symbol: "☽" },
  mars: { en: "Mars", hi: "मंगल", symbol: "♂" },
  mercury: { en: "Mercury", hi: "बुध", symbol: "☿" },
  jupiter: { en: "Jupiter", hi: "गुरु", symbol: "♃" },
  venus: { en: "Venus", hi: "शुक्र", symbol: "♀" },
  saturn: { en: "Saturn", hi: "शनि", symbol: "♄" },
  rahu: { en: "Rahu", hi: "राहु", symbol: "☊" },
  ketu: { en: "Ketu", hi: "केतु", symbol: "☋" },
};

export function NativeDisclosure({
  title,
  defaultOpen = false,
  children,
}: {
  title: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  return (
    <details className="group" open={defaultOpen}>
      <summary className="flex cursor-pointer list-none items-center justify-between rounded-lg border border-ish-border bg-ish-bg-card px-4 py-3 text-[14px] font-semibold text-ish-dark">
        {title}
        <span className="text-[18px] leading-none text-ish-accent transition-transform group-open:rotate-45">
          +
        </span>
      </summary>
      <div className="mt-3">{children}</div>
    </details>
  );
}

function SkeletonBlock({ className = "" }: { className?: string }) {
  return (
    <div className={`animate-pulse rounded-md bg-stone-200/70 ${className}`} aria-hidden="true" />
  );
}

export function PanchangResultSkeleton() {
  return (
    <div className="space-y-6" aria-label="Loading today's panchang">
      <div className="card-sacred p-6 sm:p-8">
        <div className="mb-6 text-center sm:text-left">
          <span className="block font-serif text-[32px] font-bold leading-tight text-ish-dark sm:text-[40px]">
            Today&apos;s Panchang
          </span>
          <span className="mt-2 block text-[14px] text-ish-muted">
            Calculating today&apos;s tithi, nakshatra and muhurat
          </span>
        </div>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          {[0, 1].map((item) => (
            <div key={item} className="text-center sm:text-left">
              <SkeletonBlock className="mx-auto mb-3 h-3 w-20 sm:mx-0" />
              <SkeletonBlock className="mx-auto h-8 w-36 sm:mx-0" />
              <SkeletonBlock className="mx-auto mt-2 h-3 w-44 sm:mx-0" />
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {[0, 1, 2, 3].map((item) => (
          <div key={item} className="card-sacred p-4 text-center">
            <SkeletonBlock className="mx-auto h-3 w-20" />
            <SkeletonBlock className="mx-auto mt-3 h-5 w-16" />
          </div>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-3">
        {[0, 1].map((item) => (
          <div key={item} className="card-sacred p-4 text-center">
            <SkeletonBlock className="mx-auto h-3 w-16" />
            <SkeletonBlock className="mx-auto mt-3 h-5 w-24" />
          </div>
        ))}
      </div>

      <div className="card-sacred p-6 sm:p-8">
        <SkeletonBlock className="mb-5 h-6 w-44" />
        <div className="space-y-2">
          {[0, 1, 2, 3].map((item) => (
            <SkeletonBlock key={item} className="h-12" />
          ))}
        </div>
      </div>

      <div>
        <SkeletonBlock className="h-12 w-full" />
        <div className="card-sacred mt-3 space-y-2 p-6 sm:p-8">
          {[0, 1, 2, 3, 4, 5, 6].map((item) => (
            <SkeletonBlock key={item} className="h-14" />
          ))}
        </div>
      </div>

      <SkeletonBlock className="mx-auto h-11 w-52" />
    </div>
  );
}

// ---------------------------------------------------------------------------------

export function TithiNakshatraCard({ result }: { result: PanchangResponse }) {
  return (
    <div className="card-sacred p-6 sm:p-8">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <dl className="text-center sm:text-left">
          <dt className="block text-[11px] font-semibold uppercase tracking-wider mb-2 text-ish-faint">
            Tithi
          </dt>
          <dd>
            <span className="block text-[22px] font-bold text-ish-dark font-serif">
              {result.tithi.name}
            </span>
            <span className="block text-[13px] mt-1 text-ish-muted">
              {result.tithi.paksha} Paksha &middot; Tithi {result.tithi.number}
            </span>
            {result.tithi.end_time ? (
              <span className="block text-[12px] mt-0.5 text-ish-faint">
                upto {result.tithi.end_time}
              </span>
            ) : null}
          </dd>
        </dl>
        <dl className="text-center sm:text-left">
          <dt className="block text-[11px] font-semibold uppercase tracking-wider mb-2 text-ish-faint">
            Nakshatra
          </dt>
          <dd>
            <span className="block">
              <NakshatraLabel nakshatraId={result.moon_nakshatra} />
            </span>
            <span className="block text-[13px] mt-1 capitalize text-ish-muted">
              Moon in{" "}
              <RashiLabel
                rashiId={result.moon_rashi}
                className="text-[13px] capitalize text-ish-muted font-medium"
              />{" "}
              ({RASHI_HINDI[result.moon_rashi] ?? ""})
            </span>
            {result.nakshatra.end_time ? (
              <span className="block text-[12px] mt-0.5 text-ish-faint">
                upto {result.nakshatra.end_time}
              </span>
            ) : null}
          </dd>
        </dl>
      </div>
    </div>
  );
}

export function QuickInfoGrid({ result }: { result: PanchangResponse }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
      <dl className="card-sacred p-4 text-center">
        <dt className="block text-[11px] font-semibold uppercase tracking-wider mb-1 text-ish-faint">
          Moon Rashi
        </dt>
        <dd>
          <RashiLabel rashiId={result.moon_rashi} />
        </dd>
      </dl>
      <dl className="card-sacred p-4 text-center">
        <dt className="block text-[11px] font-semibold uppercase tracking-wider mb-1 text-ish-faint">
          Sun Rashi
        </dt>
        <dd>
          <RashiLabel rashiId={result.sun_rashi} />
        </dd>
      </dl>
      <dl className="card-sacred p-4 text-center">
        <dt className="block text-[11px] font-semibold uppercase tracking-wider mb-1 text-ish-faint">
          Lagna
        </dt>
        <dd>
          <RashiLabel rashiId={result.lagna_rashi} />
        </dd>
      </dl>
      <dl className="card-sacred p-4 text-center">
        <dt className="block text-[11px] font-semibold uppercase tracking-wider mb-1 text-ish-faint">
          Paksha
        </dt>
        <dd className="text-[15px] font-bold capitalize text-ish-dark">{result.tithi.paksha}</dd>
      </dl>
    </div>
  );
}

export function YogaKaranaRow({ result }: { result: PanchangResponse }) {
  return (
    <div className="grid grid-cols-2 gap-3">
      <dl className="card-sacred p-4 text-center">
        <dt className="block text-[11px] font-semibold uppercase tracking-wider mb-1 text-ish-faint">
          Yoga
        </dt>
        <dd>
          <span className="text-[15px] font-bold text-ish-dark">{result.yoga.name}</span>
          {result.yoga.end_time ? (
            <span className="block text-[11px] mt-0.5 text-ish-faint">
              upto {result.yoga.end_time}
            </span>
          ) : null}
        </dd>
      </dl>
      <dl className="card-sacred p-4 text-center">
        <dt className="block text-[11px] font-semibold uppercase tracking-wider mb-1 text-ish-faint">
          Karana
        </dt>
        <dd className="text-[15px] font-bold text-ish-dark">{result.karana.name}</dd>
      </dl>
    </div>
  );
}

export function SunMoonTimingsCard({ result }: { result: PanchangResponse }) {
  const items: ReadonlyArray<readonly [string, string | null | undefined]> = [
    ["Sunrise", result.sunrise],
    ["Sunset", result.sunset],
    ["Moonrise", result.moonrise],
    ["Moonset", result.moonset],
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
      {items.map(([label, value]) => (
        <dl key={label} className="card-sacred p-4 text-center">
          <dt className="block text-[11px] font-semibold uppercase tracking-wider mb-1 text-ish-faint">
            {label}
          </dt>
          <dd className="text-[15px] font-bold text-ish-dark">{value ?? "—"}</dd>
        </dl>
      ))}
    </div>
  );
}

export function InauspiciousPeriodsCard({ result }: { result: PanchangResponse }) {
  const periods = [
    { label: "Rahu Kaal", period: result.rahu_kaal },
    { label: "Yamaghanta", period: result.yamaghanta },
    { label: "Gulika Kaal", period: result.gulika_kaal },
  ];
  return (
    <div className="card-sacred p-6 sm:p-8">
      <h2 className="text-[18px] font-bold mb-4 text-ish-dark font-serif">
        Auspicious &amp; Inauspicious Periods
      </h2>
      <dl className="space-y-2">
        {periods.map(({ label, period }) => (
          <div
            key={label}
            className="flex items-center justify-between p-3 rounded-lg bg-error/4 border border-error/12"
          >
            <dt className="text-[14px] font-semibold text-ish-dark">{label}</dt>
            <dd className="text-[13px] text-ish-muted">
              {period.start} – {period.end}
            </dd>
          </div>
        ))}
        {result.brahma_muhurat ? (
          <div className="flex items-center justify-between p-3 rounded-lg bg-success/4 border border-success/15">
            <dt className="text-[14px] font-semibold text-ish-dark">Brahma Muhurat ✓</dt>
            <dd className="text-[13px] text-ish-muted">
              {result.brahma_muhurat.start} – {result.brahma_muhurat.end}
            </dd>
          </div>
        ) : null}
        <div className="flex items-center justify-between p-3 rounded-lg bg-success/4 border border-success/15">
          <dt className="text-[14px] font-semibold text-ish-dark">Abhijit Muhurta ✓</dt>
          <dd className="text-[13px] text-ish-muted">
            {result.abhijit_muhurta.start} – {result.abhijit_muhurta.end}
          </dd>
        </div>
      </dl>
    </div>
  );
}

function PlanetRow({ planet }: { planet: PanchangPlanet }) {
  const label = PLANET_LABELS[planet.planet_id];
  if (!label) return null;
  return (
    <div className="flex items-center justify-between p-3 rounded-lg bg-black/2 border border-ish-border-light">
      <PlanetLabel
        planetId={planet.planet_id}
        symbol={label.symbol}
        labelEn={label.en}
        labelHi={label.hi}
      />
      <div className="text-right">
        <RashiPositionLabel rashiId={planet.rashi} />
        <span className="text-[12px] ml-1.5 text-ish-faint">
          {planet.rashi_degree.toFixed(1)}&deg;
        </span>
        {planet.is_retrograde && (
          <span className="ml-1.5 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-error/10 text-error">
            R
          </span>
        )}
      </div>
    </div>
  );
}

export function PlanetPositionsDisclosure({ result }: { result: PanchangResponse }) {
  return (
    <NativeDisclosure title="Planetary Positions" defaultOpen>
      <div className="card-sacred p-6 sm:p-8 mt-0 space-y-2">
        {result.planets
          .filter((p) => PLANET_LABELS[p.planet_id])
          .map((planet: PanchangPlanet) => (
            <PlanetRow key={planet.planet_id} planet={planet} />
          ))}
      </div>
    </NativeDisclosure>
  );
}

export function WhatIsPanchangDisclosure() {
  return (
    <NativeDisclosure title="What is Panchang?">
      <div className="card-sacred p-6 sm:p-8 mt-0">
        <div className="space-y-3 text-[14px] leading-relaxed text-ish-muted">
          <p>
            <strong className="text-ish-dark">Panchang</strong> (literally &quot;five limbs&quot;)
            is the traditional Hindu calendar and almanac. It consists of five elements: Tithi
            (lunar day), Nakshatra (lunar mansion), Yoga (luni-solar combination), Karana (half of
            tithi), and Vara (weekday). It is used daily to determine auspicious timings for
            rituals, ceremonies, and important activities.
          </p>
          <p>
            The <strong className="text-ish-dark">Tithi</strong> is calculated from the angular
            distance between the Sun and Moon. Each 12 degrees of separation equals one tithi,
            giving 30 tithis in a lunar month &mdash; 15 in the waxing phase (Shukla Paksha) and 15
            in the waning phase (Krishna Paksha).
          </p>
          <p>
            Checking the daily panchang is an integral part of Hindu tradition. It helps in
            selecting muhurtas (auspicious moments) for starting new ventures, performing puja,
            fasting on specific tithis like Ekadashi, and understanding the planetary influences of
            the day.
          </p>
        </div>
      </div>
    </NativeDisclosure>
  );
}
